import { CheckableSelectItem, DropdownSelect } from '@dixa/lib-shared-habla';
import type { ReadSearchApiArg } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/kraken';
import { toBoolean } from 'utils/formatting/string';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ConversationsFilterDropdownItem } from '../../conversationsFiltersToolbar.types';

type ConversationsQueueFilterDropdownItem = ConversationsFilterDropdownItem<
  string | number
>;

const staticDropdownItems: readonly ConversationsQueueFilterDropdownItem[] = [
  { id: 0, name: 'No queue' },
  { id: 1, name: 'In queue' },
];

/** Represents the component for filtering the conversations by queue. */
export const ConversationsQueueFilter = (): JSX.Element => {
  const {
    params: { queue: queueParam, queued: queuedParam },
    patchParams,
    unsetParams,
  } = useAppSearchParams('/conversations');

  const isQueued = queuedParam ? toBoolean(queuedParam) : undefined;

  const queuesParam = useMemo(
    () =>
      typeof queueParam !== 'undefined'
        ? typeof queueParam === 'string'
          ? [queueParam]
          : queueParam
        : [],
    [queueParam],
  );

  const initialArg: ReadSearchApiArg<'queues'> = useMemo(
    () => ({
      scope: 'queues',
      sortBy: { field: 'name', order: 'asc' },
    }),
    [],
  );

  const [readSearchQueues, { results: queues, hasNextPage, fetchNextPage }] =
    useLazyReadSearchInfiniteQuery<'queues'>();

  const [searchTerm, setSearchTerm] = useState<string>();

  useUpdateEffect(() => {
    readSearchQueues(
      {
        ...initialArg,
        query: searchTerm,
      },
      { debounceTime: 300 },
    );
  }, [readSearchQueues, initialArg, searchTerm]);

  const dropdownItems = useMemo<
    readonly ConversationsQueueFilterDropdownItem[]
  >(
    () => [
      ...staticDropdownItems,
      ...queues.map(({ id, name }) => ({ id, name })),
    ],
    [queues],
  );

  const selectedDropdownItemIds = useMemo<
    readonly ConversationsQueueFilterDropdownItem['id'][]
  >(
    () =>
      isQueued === undefined
        ? queuesParam
        : staticDropdownItems
            .filter(({ id }) => Boolean(id) === isQueued)
            .map(({ id }) => id),
    [queuesParam, isQueued],
  );

  const handleSearch = useCallback((value?: string) => {
    setSearchTerm(value?.trim() || undefined);
  }, []);

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !queues.length) {
        readSearchQueues({
          ...initialArg,
          query: searchTerm,
        });
      } else {
        setSearchTerm(undefined);
      }
    },
    [readSearchQueues, initialArg, searchTerm, queues.length],
  );

  const handleChange = useCallback(
    (item: ConversationsQueueFilterDropdownItem, checked: boolean) => {
      const isQueue = typeof item.id === 'string';

      patchParams({
        set: {
          ...(isQueue
            ? {
                queue: checked
                  ? [...queuesParam, item.id]
                  : queuesParam.filter((id) => item.id !== id),
                queued: undefined,
              }
            : {
                queue: undefined,
                queued: checked ? Boolean(item.id).toString() : undefined,
              }),
        },
        unset: ['limit', 'offset'],
      });

      ampli.conversationsFilterToggled({
        filter: 'queue',
        value: isQueue ? 'id' : Boolean(item.id) ? 'queued' : 'not_queued',
        checked,
      });
    },
    [queuesParam, patchParams],
  );

  const handleClear = useCallback(() => {
    unsetParams(['queue', 'queued', 'limit', 'offset']);
    ampli.conversationsFilterCleared({ filter: 'queue' });
  }, [unsetParams]);

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      // TODO: [TEAMS-9726] Remove re-casting when fixed in Habla
      items={dropdownItems as ConversationsQueueFilterDropdownItem[]}
      selectedItems={
        selectedDropdownItemIds as ConversationsQueueFilterDropdownItem['id'][]
      }
      itemComponent={CheckableSelectItem}
      buttonProps={{
        variant: 'tertiary',
        label: selectedDropdownItemIds.length || undefined,
      }}
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(fetchNextPage()),
      }}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      Queues
    </DropdownSelect>
  );
};
